// Main Inventory Hook

import {useCallback, useEffect, useState, useRef, useMemo} from 'react';
import {useInventoryStore} from '../store/inventoryStore';
import {InventoryFactory} from '../store/inventoryFactory';
import {InventoryFilters, AddStockForm, InventoryOverviewResponse} from '../models/inventory.model';
import {PERFORMANCE_CONFIG, PAGINATION_CONFIG} from '../../../constants/appConfig';

export const useInventory = () => {
  const {
    inventoryItems,
    locations,
    isLoading,
    error,
    filters,
    selectedItem,
    currentPage,
    itemsPerPage,
    setInventoryItems,
    setLocations,
    setLoading,
    setError,
    setFilters,
    setSelectedItem,
    setPagination,
    lowStockItems,
    outOfStockItems,
    filteredItems,
    resetFilters,
    resetStore
  } = useInventoryStore();

  // Enhanced inventory overview state with caching
  const [inventoryOverview, setInventoryOverview] = useState<InventoryOverviewResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewCache, setOverviewCache] = useState<Map<string, { data: InventoryOverviewResponse; timestamp: number }>>(new Map());

  // Cache timeout from configuration
  const CACHE_TIMEOUT = PERFORMANCE_CONFIG.CACHE_DURATION;

  // Request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  const isRequestInProgressRef = useRef(false);

  // Use Cases - Create once to prevent infinite loops
  const useCases = useMemo(() => ({
    getInventoryItemsUseCase: InventoryFactory.createGetInventoryItemsUseCase(),
    getLocationsUseCase: InventoryFactory.createGetLocationsUseCase(),
    addStockUseCase: InventoryFactory.createAddStockUseCase(),
    getInventoryStatisticsUseCase: InventoryFactory.createGetInventoryStatisticsUseCase(),
    getInventoryMovementsUseCase: InventoryFactory.createGetInventoryMovementsUseCase(),
    getComprehensiveReportUseCase: InventoryFactory.createGetComprehensiveReportUseCase(),
    getTrackedSerialsUseCase: InventoryFactory.createGetTrackedSerialsUseCase(),
    getSerialStatisticsUseCase: InventoryFactory.createGetSerialStatisticsUseCase(),
    deleteSerialUseCase: InventoryFactory.createDeleteSerialUseCase(),
    getSerialTimelineUseCase: InventoryFactory.createGetSerialTimelineUseCase(),
    getSerialDetailsUseCase: InventoryFactory.createGetSerialDetailsUseCase()
  }), []);

  // Fetch inventory items
  const fetchInventoryItems = useCallback(async (customFilters?: InventoryFilters, page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const filtersToUse = customFilters || filters;
      const items = await useCases.getInventoryItemsUseCase.execute(filtersToUse, page, itemsPerPage);

      setInventoryItems(items);
      setPagination(page, Math.ceil(items.length / itemsPerPage));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch inventory items');
    } finally {
      setLoading(false);
    }
  }, [useCases.getInventoryItemsUseCase, setInventoryItems, setLoading, setError, setPagination, filters, itemsPerPage]);

  // Fetch locations
  const fetchLocations = useCallback(async () => {
    try {
      const locations = await useCases.getLocationsUseCase.execute();
      setLocations(locations);
    } catch (error) {
      // Don't set error for locations as it's not critical
    }
  }, [useCases.getLocationsUseCase, setLocations]);

  // Generate cache key for overview data
  const generateCacheKey = (filters: any, page: number, limit: number) => {
    return JSON.stringify({filters, page, limit});
  };

  // Check if cache is valid
  const isCacheValid = (timestamp: number) => {
    return Date.now() - timestamp < CACHE_TIMEOUT;
  };

  // Fetch enhanced inventory overview with caching and debouncing
  const fetchInventoryOverview = useCallback(async (filters?: any, page = 1, limit = 20) => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Prevent multiple simultaneous requests
    if (isRequestInProgressRef.current) {
      return null;
    }

    try {
      isRequestInProgressRef.current = true;
      abortControllerRef.current = new AbortController();

      setOverviewLoading(true);
      setError(null);

      // Check cache first
      const cacheKey = generateCacheKey(filters, page, limit);
      const cachedData = overviewCache.get(cacheKey);

      if (cachedData && isCacheValid(cachedData.timestamp)) {
        setInventoryOverview(cachedData.data);
        return cachedData.data;
      }

      const response = await useCases.getComprehensiveReportUseCase.execute(filters, page, limit);

      // Only update if request wasn't aborted
      if (!abortControllerRef.current?.signal.aborted) {
         // Normalize: if backend returned raw array, adapt to { items, ... }
         const normalized: InventoryOverviewResponse = Array.isArray(response)
           ? {items: response, total_items: response.length, total_quantity: 0, total_value: 0, low_stock_count: 0, out_of_stock_count: 0}
           : response;
         setInventoryOverview(normalized);

        // Cache the response
        setOverviewCache(prev => {
          const newCache = new Map(prev);
          newCache.set(cacheKey, {data: normalized, timestamp: Date.now()});
          return newCache;
        });

        return normalized;
      }

      return null;
    } catch (error) {
      if (!abortControllerRef.current?.signal.aborted) {
        setError(error instanceof Error ? error.message : 'Failed to fetch inventory overview');
      }
      return null;
    } finally {
      isRequestInProgressRef.current = false;
      setOverviewLoading(false);
    }
  }, [useCases.getComprehensiveReportUseCase, setError, overviewCache]);

  // Clear cache
  const clearOverviewCache = useCallback(() => {
    setOverviewCache(new Map());
  }, []);

  // Add stock
  const addStock = useCallback(async (stockData: AddStockForm) => {
    try {
      setLoading(true);
      setError(null);

      await useCases.addStockUseCase.execute(stockData);

      // Clear cache when data changes
      clearOverviewCache();

      // Refresh inventory items after adding stock
      try {
        const items = await useCases.getInventoryItemsUseCase.execute(filters, currentPage, itemsPerPage);
        setInventoryItems(items);
        setPagination(currentPage, Math.ceil(items.length / itemsPerPage));
      } catch (refreshError) {
        // Silently ignore refresh errors - stock was already added successfully
        // Refresh is non-critical for the add stock operation
      }

      return true;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to add stock');
      return false;
    } finally {
      setLoading(false);
    }
  }, [useCases.addStockUseCase, setLoading, setError, filters, currentPage, itemsPerPage, useCases.getInventoryItemsUseCase, setInventoryItems, setPagination, clearOverviewCache]);

  // Get inventory statistics
  const getInventoryStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const statistics = await useCases.getInventoryStatisticsUseCase.execute();
      return statistics;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to get inventory statistics');
      return null;
    } finally {
      setLoading(false);
    }
  }, [useCases.getInventoryStatisticsUseCase, setLoading, setError]);

  // Get inventory movements
  const getInventoryMovements = useCallback(async (filters?: any, page = 1, limit = 20) => {
    try {
      setLoading(true);
      setError(null);
      const result = await useCases.getInventoryMovementsUseCase.execute(filters, page, limit);
      return result;
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to get inventory movements');
      return {items: [], pagination: undefined};
    } finally {
      setLoading(false);
    }
  }, [useCases.getInventoryMovementsUseCase, setLoading, setError]);

  // Update filters with debouncing
  const updateFilters = useCallback(async (newFilters: Partial<InventoryFilters>) => {
    const updatedFilters = {...filters, ...newFilters};
    setFilters(newFilters);

    // Auto-fetch with new filters
    try {
      setLoading(true);
      setError(null);
      const items = await useCases.getInventoryItemsUseCase.execute(updatedFilters, 1, itemsPerPage);
      setInventoryItems(items);
      setPagination(1, Math.ceil(items.length / itemsPerPage));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update filters');
    } finally {
      setLoading(false);
    }
  }, [filters, setFilters, useCases.getInventoryItemsUseCase, itemsPerPage, setLoading, setError, setInventoryItems, setPagination]);

  // Go to page
  const goToPage = useCallback(async (page: number) => {
    try {
      setLoading(true);
      setError(null);
      const items = await useCases.getInventoryItemsUseCase.execute(filters, page, itemsPerPage);
      setInventoryItems(items);
      setPagination(page, Math.ceil(items.length / itemsPerPage));
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to go to page');
    } finally {
      setLoading(false);
    }
  }, [filters, useCases.getInventoryItemsUseCase, itemsPerPage, setLoading, setError, setInventoryItems, setPagination]);

  // Initial data load (locations disabled per requirement)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await fetchInventoryItems();
      } catch (error) {
        // Error is already handled by fetchInventoryItems which sets error state
        // This catch prevents unhandled promise rejection warning
      }
    };
    loadInitialData();
  }, [fetchInventoryItems]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Memoized computed values
  const memoizedFilteredItems = useMemo(() => filteredItems(), [filteredItems]);
  const memoizedLowStockItems = useMemo(() => lowStockItems(), [lowStockItems]);
  const memoizedOutOfStockItems = useMemo(() => outOfStockItems(), [outOfStockItems]);

  // Get serial statistics
  const getSerialStatistics = useCallback(async () => {
    try {
      return await useCases.getSerialStatisticsUseCase.execute();
    } catch (error) {
      throw error;
    }
  }, [useCases.getSerialStatisticsUseCase]);

  return {
    // Data
    inventoryItems,
    locations,
    filteredItems: memoizedFilteredItems,
    lowStockItems: memoizedLowStockItems,
    outOfStockItems: memoizedOutOfStockItems,
    selectedItem,
    inventoryOverview,

    // UI State
    isLoading,
    overviewLoading,
    error,
    filters,
    currentPage,
    itemsPerPage,

    // Actions
    fetchInventoryItems,
    fetchLocations,
    fetchInventoryOverview,
    addStock,
    getInventoryStatistics,
    getInventoryMovements,
    updateFilters,
    setSelectedItem,
    goToPage,
    resetFilters,
    resetStore,
    clearOverviewCache,

    // Serials
    getTrackedSerials: useCases.getTrackedSerialsUseCase.execute.bind(useCases.getTrackedSerialsUseCase),
    getSerialStatistics,
    deleteSerial: useCases.deleteSerialUseCase.execute.bind(useCases.deleteSerialUseCase),
    getSerialTimeline: useCases.getSerialTimelineUseCase.execute.bind(useCases.getSerialTimelineUseCase),
    getSerialDetails: useCases.getSerialDetailsUseCase.execute.bind(useCases.getSerialDetailsUseCase),

    // Stats
    totalItems: inventoryItems.length,
    lowStockCount: memoizedLowStockItems.length,
    outOfStockCount: memoizedOutOfStockItems.length
  };
};
